import { FC } from 'react';
import queryString from 'query-string';
import { Trans, useTranslation } from 'react-i18next';
import { BusinessEvents, airTracker } from '@/utils/airTracker';
import { PageStateErrorState, Button, HeroWallet, TypographyTextLink, VStack } from '@airwallex/webapp-components';
import { clientGetStorage, StorageKey } from '@/utils/localStorage';
import { space } from '@airwallex/global';
import { COMPONENT_NAME_V2 } from '@/app';

export enum ErrorTypeV2 {
  LINK_EXPIRED = 'LINK_EXPIRED',
  CHECKOUT_EXPIRED = 'CHECKOUT_EXPIRED',
  DISABLED = 'DISABLED',
  PAID = 'PAID',
  DEFAULT = 'DEFAULT',
}

const ErrorPageContent: FC = () => {
  const { error } = queryString.parse(window.location.search) as { error: ErrorTypeV2 };
  const { t } = useTranslation();
  airTracker.businessLog(BusinessEvents.enterErrorPageV2, {
    component: COMPONENT_NAME_V2,
    error: error as ErrorTypeV2,
  });
  switch (error) {
    case ErrorTypeV2.PAID:
      return (
        <PageStateErrorState
          title={t('v2.paid.head')}
          body={t('v2.paid.description')}
          image={HeroWallet}
          variant="PAGE"
        />
      );
    case ErrorTypeV2.DISABLED:
      return <PageStateErrorState title={t('v2.disabled.head')} body={t('v2.disabled.description')} />;
    case ErrorTypeV2.LINK_EXPIRED:
      return <PageStateErrorState title={t('v2.linkExpired.head')} body={t('v2.linkExpired.description')} />;
    case ErrorTypeV2.CHECKOUT_EXPIRED:
      return (
        <PageStateErrorState title={t('v2.checkoutExpired.head')}>
          {/* Actions live in the `children` slot (rendered outside PageState's
              body <p>) so the original VStack layout is preserved without
              nesting a <div> inside a <p>. */}
          <VStack align="CENTER" style={{ gap: space.m }}>
            {t('v2.checkoutExpired.description')}
            <Button
              variant="PRIMARY"
              onClick={() => {
                window.location.href = clientGetStorage(StorageKey.PAYMENT_URL);
              }}
            >
              {t('checkout.error.button.try_again')}
            </Button>
          </VStack>
        </PageStateErrorState>
      );

    default:
      return (
        <PageStateErrorState title={t('v2.default.head')}>
          <VStack align="CENTER" style={{ gap: space.m }}>
            <Trans
              t={t}
              i18nKey="v2.default.description"
              parent="span"
              components={[
                <TypographyTextLink key="help-centre" href="https://help.airwallex.com/hc/en-gb/requests/new" />,
              ]}
            />
            <Button
              variant="PRIMARY"
              onClick={() => {
                window.location.href = clientGetStorage(StorageKey.PAYMENT_URL);
              }}
            >
              {t('checkout.error.button.try_again')}
            </Button>
          </VStack>
        </PageStateErrorState>
      );
  }
};

const ErrorPage: FC = () => {
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <div css={{ flex: 1 }}>
        <ErrorPageContent />
      </div>
    </div>
  );
};

export default ErrorPage;
